import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Typography, Alert, InputAdornment, IconButton,
} from '@mui/material';
import {
  LocalHospital as HospitalIcon, Visibility, VisibilityOff, CheckCircleOutline as TickIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { COLLEGE_NAME, PRODUCT_NAME } from '../branding';

const HIGHLIGHTS = [
  'Programs, batches, faculty and student records',
  'Clinical postings and hospital rotations',
  'Skills and procedure log book with sign-off',
  'Attendance, examinations, fees and hostel',
];

export default function Login() {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(credentials);
    if (result.success) navigate('/admin');
    else setError(result.message);
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f4f8f7' }}>
      {/* Brand panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1.1,
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(160deg, #004d40 0%, #00695c 55%, #00897b 100%)',
        }}
      >
        <Box sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', top: -140, right: -120 }} />
        <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', bottom: -100, left: -80 }} />
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ width: 76, height: 76, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <HospitalIcon sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 1 }}>{COLLEGE_NAME}</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, mb: 5 }}>{PRODUCT_NAME}</Typography>
          {HIGHLIGHTS.map((h) => (
            <Box key={h} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <TickIcon sx={{ color: '#a7ffeb' }} />
              <Typography sx={{ opacity: 0.92 }}>{h}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Sign-in panel */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, width: '100%', maxWidth: 440, borderRadius: 4, border: '1px solid #e0ece9' }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
            <HospitalIcon color="primary" sx={{ fontSize: 34 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{COLLEGE_NAME}</Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Welcome back</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>Sign in to the admin console</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal" required fullWidth autoFocus label="Username" autoComplete="username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            />
            <TextField
              margin="normal" required fullWidth label="Password" autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowPassword(!showPassword)} aria-label="Show or hide password">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3, py: 1.4 }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
            Faculty and students sign in through their mobile apps.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
